// Shared types for the walkable-map engine (spec §19). A "world" (prison, ABC) is
// pure data — rooms, tiles, spots, palette and copy — fed to one <RoomEngine>. The
// engine logic is identical across styles; only this config differs.

export const TILE = 24; // px per tile

export type Dir = 'u' | 'd' | 'l' | 'r';

export interface Exit {
  to?: string; // another room key…
  route?: string; // …or an app route (leaves the world)
  r?: number;
  c?: number;
  face2?: Dir;
  kind: 'door' | 'stair';
  face: Dir;
  glyph: string;
  name: string;
}

export interface Room {
  name: string;
  floor: string;
  map: string[];
  exits: Record<string, Exit>;
  entry: [number, number];
  /** "r,c" -> NPC slot key (which roster player fills it is decided at runtime). */
  npc: Record<string, string>;
  blurb: string;
}

/** An action spot maps a map char to a gameplay route + the phase it's live in. */
export interface Spot {
  key: string;
  name: string;
  sub: string;
  route: string;
  phase: 'SUBMITTING' | 'VOTING';
  doneField?: 'submitted' | 'points_done' | 'veto_done' | 'guesses_done';
  shut: string;
}

/** An info spot is always interactable (marker "?"), for non-action screens
 *  (the rounds schedule, the table) — distinct from the "!" action spots. */
export interface InfoSpot {
  key: string;
  name: string;
  sub: string;
  route: string;
}

/** A static scene: a fixed tableau of archetype actors in a room, shown only when
 *  the round is in the required state. Blocks the tiles it occupies; A gives its
 *  next line. `requires` maps to real round phase — 'voted'→VOTING, 'unvoted'→
 *  SUBMITTING — so the map reflects the round, never lying about it (spec §27). */
export interface Scene {
  id: string;
  room: string;
  requires: 'voted' | 'unvoted' | null;
  actors: Record<string, string>; // "r,c" -> SCENE_SPRITES key
  lines: string[];
}

/** A moving or animated scene actor (spec §34). role 'hold' = pinned, cycles
 *  frames, blocks; 'flee' = moves away from `from`; 'chase' = moves toward
 *  `target`, never landing on it. Movers don't block. */
export interface PatrolActor {
  k: string; // sprite key (from SCENE_SPRITES)
  role: 'hold' | 'flee' | 'chase';
  at?: [number, number];
  frames?: string[]; // two poses, cycled on the tick
  from?: string; // flee: the actor key to flee from
  target?: string; // chase: the actor key to close on
  bubble?: string; // one word, floated above
}

export interface Patrol {
  id: string;
  room: string;
  requires: 'voted' | 'served' | 'unvoted' | null; // 'voted'/'served' -> VOTING
  ms: number; // tick interval
  area?: { r: [number, number]; c: [number, number] }; // roam bounds (movers)
  cast: PatrolActor[];
  lines: string[];
}

export interface SpritePalette {
  id: string; // cache namespace so worlds don't share rendered sprites
  disc: string; // colour of the disc behind other players
  pal: Record<string, string>; // chars 1/2/3 for other players
  palMe: Record<string, string>; // brighter ramp for "you"
}

export interface MenuLink {
  label: string;
  sub: string;
  route: string;
  ex?: boolean; // styled as the "exit / switch venue" row
}

/** The ambient jukebox is synthesised in-browser (no audio files). It plays a
 *  chord progression (pad + bass + melody), so it's consonant and actually musical. */
export interface JukeboxConfig {
  root: number; // root frequency in Hz
  chords: number[][]; // progression; each chord = semitone offsets from root
  beatMs: number; // time per beat (4 beats per bar / chord)
  wave: OscillatorType;
  gain: number; // master volume (keep low; it's background)
}

export interface WorldConfig {
  /** Root class the engine scopes its CSS under (prison.css / abc.css). */
  wrapperClass: string;
  menuTitle: string;
  rooms: Record<string, Room>;
  spots: Record<string, Spot>;
  info: Record<string, InfoSpot>;
  cls: Record<string, string>; // map char -> tile CSS class
  walk: Set<string>;
  floor: Record<string, string>; // room key -> floor tile class
  npcSlots: string[];
  palette: SpritePalette;
  npcLines: string[];
  /** Flavour text when you inspect a fitting: map char -> {title,text}. */
  flavor: Record<string, { title: string; text: string }>;
  /** Extra rows under the room list in the START menu (regime/gig list, chat, switch). */
  menuLinks: MenuLink[];
  /** Fallback dialogue title when inspecting empty floor. */
  roomVoice?: string;
  /** Ambient background music for this world. */
  jukebox: JukeboxConfig;
  /** Static scene tableaux (spec §27). */
  scenes?: Scene[];
  /** Moving/animated scenes (spec §34). */
  patrols?: Patrol[];
}
